// Firestore tracking service for comprehensive user and order data
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  addDoc,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { app, auth } from '../firebase/firebaseConfig';

const db = getFirestore(app);

const USERS_COLLECTION = 'users';
const ORDERS_COLLECTION = 'orders';
const FILES_COLLECTION = 'files';
const STATUS_COLLECTION = 'status';
const ORDER_EVENTS_SUBCOLLECTION = 'events';

const SESSION_STORAGE_KEYS = {
  sessionId: 'sessionId',
  orderId: 'orderId',
  orderOwner: 'orderOwner',
};

const PIPELINE_VERSION = 'v2';

const CANONICAL_STATUSES = new Set(['pending', 'processing', 'completed', 'failed']);

const STATUS_ALIASES = {
  uploaded: 'completed',
  paid: 'completed',
  unpaid: 'pending',
  fulfilled: 'completed',
  draft: 'pending',
  success: 'completed',
  error: 'failed',
  cancelled: 'failed',
  canceled: 'failed',
};

const EVENT_ALIASES = {
  session_start: 'session.started',
  session_end: 'session.ended',
  page_view: 'session.page_view',
  upload_start: 'upload.started',
  upload_complete: 'upload.completed',
  file_uploaded: 'upload.completed',
  processing_start: 'processing.started',
  processing_complete: 'processing.completed',
  payment_start: 'payment.started',
  payment_complete: 'payment.completed',
  download: 'download.completed',
  file_downloaded: 'download.completed',
  error: 'error.processing',
};

function normalizeStatus(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'pending';
  if (CANONICAL_STATUSES.has(raw)) return raw;
  return STATUS_ALIASES[raw] || 'pending';
}

function normalizePaymentAmountValue(value, fallback = 0) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsed =
    typeof value === 'number'
      ? value
      : Number.parseFloat(String(value).replace(',', '.'));

  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizePipelineType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'processing';
  if (raw === 'delivery') return 'download';
  if (raw === 'uploads') return 'upload';
  return raw;
}

function normalizeEventType(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'session.started';

  const lowered = raw.toLowerCase();
  if (EVENT_ALIASES[lowered]) {
    return EVENT_ALIASES[lowered];
  }

  if (lowered.startsWith('processing_')) {
    const status = normalizeStatus(lowered.replace('processing_', ''));
    if (status === 'failed') return 'error.processing';
    return status === 'completed' ? 'processing.completed' : 'processing.started';
  }

  if (lowered.startsWith('payment_')) {
    const status = normalizeStatus(lowered.replace('payment_', ''));
    if (status === 'failed') return 'error.payment';
    return status === 'completed' ? 'payment.completed' : 'payment.started';
  }

  if (lowered.startsWith('upload_')) {
    const status = normalizeStatus(lowered.replace('upload_', ''));
    return status === 'completed' ? 'upload.completed' : 'upload.started';
  }

  return lowered.replace(/_/g, '.');
}

function stripUndefinedDeep(value) {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  const cleaned = {};
  Object.keys(value).forEach((key) => {
    const normalizedValue = stripUndefinedDeep(value[key]);
    if (normalizedValue !== undefined) {
      cleaned[key] = normalizedValue;
    }
  });

  return cleaned;
}

function extractPipelineStepFromEvent(eventType) {
  const canonical = normalizeEventType(eventType);
  return canonical.split('.')[0] || 'session';
}

function buildCanonicalContext({ userId, sessionId, orderId, fileName, filePath } = {}) {
  const resolvedUserId = userId || auth.currentUser?.uid || null;
  const resolvedSessionId = sessionId || getCachedValue(SESSION_STORAGE_KEYS.sessionId) || resolveSessionId();
  let resolvedOrderId = orderId || getCachedValue(SESSION_STORAGE_KEYS.orderId) || null;

  if (!resolvedOrderId && resolvedUserId && resolvedSessionId) {
    resolvedOrderId = `${resolvedUserId}_${resolvedSessionId}`;
  }

  return {
    userId: resolvedUserId,
    sessionId: resolvedSessionId,
    orderId: resolvedOrderId,
    fileName: fileName || null,
    filePath: filePath || null,
    fileId: resolvedUserId && fileName ? `${resolvedUserId}_${fileName}` : null,
    pipelineVersion: PIPELINE_VERSION,
  };
}

/**
 * Wait for auth to be ready
 */
function waitForAuth() {
  return new Promise((resolve, reject) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        resolve(user);
      } else {
        reject(new Error('User not authenticated'));
      }
    });

    // Timeout after 10 seconds
    setTimeout(() => {
      unsubscribe();
      reject(new Error('Auth state check timeout'));
    }, 10000);
  });
}

/**
 * Generate a session ID for tracking user sessions
 */
export function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getCachedValue(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn('Unable to read from sessionStorage', error);
    return null;
  }
}

function clearCachedOrderIdentifiers() {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.orderId);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.orderOwner);
  } catch (error) {
    console.warn('Unable to clear cached order identifiers', error);
  }
}

function rememberSessionDetails({ sessionId, orderId, userId } = {}) {
  try {
    if (sessionId) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionId, sessionId);
    }
    if (orderId) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.orderId, orderId);
    }
    if (userId) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.orderOwner, userId);
    }
  } catch (error) {
    console.warn('Unable to persist session/order identifiers', error);
  }
}

function resolveSessionId(preferredSessionId) {
  const storedSessionId = getCachedValue(SESSION_STORAGE_KEYS.sessionId);
  const resolvedSessionId = preferredSessionId || storedSessionId || generateSessionId();

  if (!storedSessionId || storedSessionId !== resolvedSessionId) {
    rememberSessionDetails({ sessionId: resolvedSessionId });
  }

  return resolvedSessionId;
}

function resolveOrderId(userId, sessionId, explicitOrderId) {
  if (!userId) {
    throw new Error('Cannot generate orderId without userId');
  }

  if (explicitOrderId) {
    return explicitOrderId;
  }

  const storedOrderId = getCachedValue(SESSION_STORAGE_KEYS.orderId);
  const storedOrderOwner = getCachedValue(SESSION_STORAGE_KEYS.orderOwner);

  if (storedOrderId && storedOrderOwner === userId) {
    return storedOrderId;
  }

  if (
    (storedOrderId && !storedOrderOwner) ||
    (storedOrderId && storedOrderOwner && storedOrderOwner !== userId)
  ) {
    clearCachedOrderIdentifiers();
  }

  return `${userId}_${sessionId}`;
}

function extractOrderOwner(data = {}) {
  return data?.order?.userId || data?.userId || null;
}

async function ensureOrderContext({ userId, sessionId, explicitOrderId, bootstrap = {} }) {
  console.log('ensureOrderContext called with userId:', userId, 'auth.currentUser:', auth.currentUser?.uid);

  if (!userId) {
    throw new Error('ensureOrderContext requires a valid userId');
  }

  let resolvedSessionId = resolveSessionId(sessionId);
  let resolvedOrderId = resolveOrderId(userId, resolvedSessionId, explicitOrderId);
  let orderRef = doc(db, ORDERS_COLLECTION, resolvedOrderId);

  let orderSnapshot;
  let orderExists = false;
  
  // Try to read the document, but handle the case where it doesn't exist
  // or user doesn't have permission to read it yet
  try {
    orderSnapshot = await getDoc(orderRef);
    orderExists = orderSnapshot.exists();
  } catch (error) {
    // If we get a permission error, the document likely doesn't exist
    // We'll try to create it
    console.log('Could not read order document, will attempt to create:', error.message);
    orderExists = false;
    orderSnapshot = null;
  }

  let ownerId = orderExists ? extractOrderOwner(orderSnapshot.data()) : null;

  const ownershipMismatch = ownerId && ownerId !== userId;
  const missingOwner = orderExists && !ownerId;

  if ((ownershipMismatch || missingOwner) && explicitOrderId) {
    throw new Error('Requested order is not owned by the current user');
  }

  if (ownershipMismatch || missingOwner) {
    clearCachedOrderIdentifiers();
    resolvedSessionId = generateSessionId();
    rememberSessionDetails({ sessionId: resolvedSessionId, userId });
    resolvedOrderId = `${userId}_${resolvedSessionId}`;
    orderRef = doc(db, ORDERS_COLLECTION, resolvedOrderId);
    
    try {
      orderSnapshot = await getDoc(orderRef);
      orderExists = orderSnapshot.exists();
    } catch (error) {
      orderExists = false;
      orderSnapshot = null;
    }
    ownerId = null;
  }

  if (!orderExists) {
    const createdAt = serverTimestamp();
    const baseDoc = {
      userId,  // CRITICAL: userId at top level for security rules
      sessionId: resolvedSessionId,
      order: {
        orderId: resolvedOrderId,
        userId,
        sessionId: resolvedSessionId,
        status: 'draft',
        stageTimestamps: {
          created: createdAt,
        },
      },
      files: [],
      product: {},
      payment: {
        status: 'pending',
        amount: 0,
        currency: 'EUR',
      },
      delivery: {
        downloadCount: 0,
      },
      flags: {
        needsAttention: false,
        isTest: false,
        fraudScore: 0,
      },
      audit: {
        createdAt,
        updatedAt: createdAt,
        createdBy: userId,
        updatedBy: userId,
        source: 'web_client',
      },
      ...bootstrap,
    };

    // Use setDoc WITHOUT merge for new documents - this triggers 'create' rule
    console.log('Creating new order document:', resolvedOrderId);
    await setDoc(orderRef, baseDoc);  // NO { merge: true } here!
    
    orderSnapshot = await getDoc(orderRef);
    ownerId = userId;
  }

  rememberSessionDetails({ sessionId: resolvedSessionId, orderId: resolvedOrderId, userId });

  return {
    orderId: resolvedOrderId,
    orderRef,
    orderData: orderSnapshot?.exists() ? orderSnapshot.data() : {},
    sessionId: resolvedSessionId,
  };
}

async function refreshOrderData(orderRef) {
  const snapshot = await getDoc(orderRef);
  return snapshot.exists() ? snapshot.data() : {};
}

function buildUserProfile(orderData, existingProfile = {}, fallbackUser = {}) {
  return {
    ...existingProfile,
    email: orderData.email ?? existingProfile.email ?? fallbackUser.email ?? '',
    displayName: orderData.fullName ?? existingProfile.displayName ?? fallbackUser.displayName ?? '',
    fullName: orderData.fullName ?? existingProfile.fullName ?? '',
    address: orderData.address ?? existingProfile.address ?? '',
    city: orderData.city ?? existingProfile.city ?? '',
    postcode: orderData.postcode ?? existingProfile.postcode ?? '',
    country: orderData.country ?? existingProfile.country ?? 'Spain',
    phone: orderData.phone ?? existingProfile.phone ?? '',
  };
}

function buildUserStats(orderData, existingStats = {}, orderId, existingOrderData = {}) {
  const stats = {
    totalOrders: existingStats.totalOrders || 0,
    totalSpent: existingStats.totalSpent || 0,
    photoCount: existingStats.photoCount || 0,
    promoCodesUsed: existingStats.promoCodesUsed || 0,
    lastOrderId: orderId || existingStats.lastOrderId || null,
  };

  const isExistingOrder = existingStats.lastOrderId === orderId;
  const hasIncomingPaymentAmount =
    orderData.paymentAmount !== undefined &&
    orderData.paymentAmount !== null &&
    orderData.paymentAmount !== '';
  const normalizedPaymentAmount = hasIncomingPaymentAmount
    ? normalizePaymentAmountValue(orderData.paymentAmount, 0)
    : null;
  const existingOrderPaymentAmount = normalizePaymentAmountValue(
    existingOrderData?.payment?.amount,
    0
  );
  const incomingPaymentStatus = normalizeStatus(
    orderData.paymentStatus || existingOrderData?.payment?.status
  );
  const existingPromoCounted =
    Boolean(existingOrderData?.product?.promoCode) &&
    normalizeStatus(existingOrderData?.payment?.status) === 'completed';

  if (orderData.photoType && !isExistingOrder) {
    stats.photoCount += 1;
    stats.totalOrders += 1;
  }

  if (normalizedPaymentAmount !== null && normalizedPaymentAmount > 0) {
    const previousAmount = isExistingOrder ? existingOrderPaymentAmount || 0 : 0;
    const delta = normalizedPaymentAmount - previousAmount;
    if (delta > 0) {
      stats.totalSpent += delta;
    }
  }

  if (orderData.promoCode && incomingPaymentStatus === 'completed' && !existingPromoCounted) {
    stats.promoCodesUsed += 1;
  }

  stats.updatedAt = serverTimestamp();
  if (!existingStats.createdAt) {
    stats.createdAt = serverTimestamp();
  }

  return stats;
}

function buildUserMetadata(existingMetadata = {}, { sessionId }) {
  const provider = auth?.currentUser?.providerData?.[0]?.providerId || existingMetadata.authProvider || 'password';

  return {
    ...existingMetadata,
    createdAt: existingMetadata.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastActivity: serverTimestamp(),
    authProvider: provider,
    roles: existingMetadata.roles || ['customer'],
    lastSessionId: sessionId || existingMetadata.lastSessionId || null,
  };
}

function buildUserPreferences(existingPreferences = {}, orderData = {}) {
  return {
    ...existingPreferences,
    language: existingPreferences.language || orderData.language || 'es',
    currency: existingPreferences.currency || orderData.currency || 'EUR',
    notifications: existingPreferences.notifications ?? { email: true },
    defaultPhotoType: orderData.photoType || existingPreferences.defaultPhotoType || 'digital',
  };
}

function buildLegacyCurrentOrder(orderData, existingCurrentOrder = {}, sessionId) {
  const legacyOrder = { ...existingCurrentOrder };

  if (!legacyOrder.createdAt) {
    legacyOrder.createdAt = serverTimestamp();
  }

  legacyOrder.sessionId = sessionId || legacyOrder.sessionId || null;
  if (orderData.photoType !== undefined) legacyOrder.photoType = orderData.photoType;
  if (orderData.paymentStatus !== undefined) legacyOrder.paymentStatus = orderData.paymentStatus;
  if (orderData.paymentMethod !== undefined) legacyOrder.paymentMethod = orderData.paymentMethod;
  if (orderData.paymentAmount !== undefined) {
    legacyOrder.paymentAmount = normalizePaymentAmountValue(
      orderData.paymentAmount,
      legacyOrder.paymentAmount || 0
    );
  }
  if (orderData.promoCode !== undefined) legacyOrder.promoCode = orderData.promoCode;
  if (orderData.promoDiscount !== undefined) legacyOrder.promoDiscount = orderData.promoDiscount;
  if (orderData.uploadedImagePath !== undefined) legacyOrder.uploadedImagePath = orderData.uploadedImagePath;
  if (orderData.processedImagePath !== undefined) legacyOrder.processedImagePath = orderData.processedImagePath;
  if (orderData.assembledImagePath !== undefined) legacyOrder.assembledImagePath = orderData.assembledImagePath;

  legacyOrder.updatedAt = serverTimestamp();

  return legacyOrder;
}

function buildProductSection(orderData, existingProduct = {}) {
  const product = { ...existingProduct };

  if (orderData.photoType !== undefined) {
    product.photoType = orderData.photoType;
  }
  if (orderData.productOptions !== undefined) {
    product.options = orderData.productOptions;
  }
  if (orderData.quantity !== undefined) {
    product.quantity = orderData.quantity;
  }
  if (orderData.paymentAmount !== undefined) {
    product.price = normalizePaymentAmountValue(
      orderData.paymentAmount,
      existingProduct.price || 0
    );
  }
  if (orderData.currency) {
    product.currency = orderData.currency;
  }
  if (orderData.promoCode !== undefined) {
    product.promoCode = orderData.promoCode;
  }
  if (orderData.promoDiscount !== undefined) {
    product.promoDiscount = orderData.promoDiscount;
  }

  if (!product.quantity) {
    product.quantity = 1;
  }

  return product;
}

function buildPaymentSection(orderData, existingPayment = {}) {
  const incomingStatus = orderData.paymentStatus || existingPayment.status || 'pending';
  const canonicalStatus = normalizeStatus(incomingStatus);
  const hasIncomingAmount =
    orderData.paymentAmount !== undefined &&
    orderData.paymentAmount !== null &&
    orderData.paymentAmount !== '';

  const payment = {
    status: canonicalStatus,
    canonicalStatus,
    legacyStatus: incomingStatus,
    method: orderData.paymentMethod || existingPayment.method || null,
    amount: hasIncomingAmount
      ? normalizePaymentAmountValue(orderData.paymentAmount, existingPayment.amount || 0)
      : existingPayment.amount ?? 0,
    currency: orderData.currency || existingPayment.currency || 'EUR',
    providerRef: orderData.paymentProviderRef || existingPayment.providerRef || null,
    attempts: existingPayment.attempts || [],
    updatedAt: serverTimestamp(),
  };

  if (!existingPayment.createdAt) {
    payment.createdAt = serverTimestamp();
  }

  if (orderData.paymentMeta) {
    payment.meta = {
      ...(existingPayment.meta || {}),
      ...orderData.paymentMeta,
      pipelineVersion: PIPELINE_VERSION,
    };
  }

  return payment;
}

function buildDeliverySection(orderData, existingDelivery = {}, fallbackEmail = '') {
  const delivery = {
    ...existingDelivery,
  };

  if (orderData.email) {
    delivery.email = orderData.email;
  } else if (!delivery.email) {
    delivery.email = fallbackEmail;
  }

  if (orderData.processedImagePath) {
    delivery.downloadUrl = orderData.processedImagePath;
  }

  if (orderData.deliveryMethod) {
    delivery.method = orderData.deliveryMethod;
  }

  if (delivery.downloadCount === undefined) {
    delivery.downloadCount = 0;
  }

  return delivery;
}

function buildFlagsSection(orderData, existingFlags = {}) {
  return {
    needsAttention: orderData.needsAttention ?? existingFlags.needsAttention ?? false,
    isTest: orderData.isTest ?? existingFlags.isTest ?? false,
    fraudScore: orderData.fraudScore ?? existingFlags.fraudScore ?? 0,
  };
}

async function recordOrderEvent(orderId, userId, eventType, payload = {}) {
  try {
    const canonicalEventType = normalizeEventType(eventType);
    await addDoc(collection(db, ORDERS_COLLECTION, orderId, ORDER_EVENTS_SUBCOLLECTION), {
      orderId,
      userId,
      eventType: canonicalEventType,
      canonicalEventType,
      legacyEventType: canonicalEventType === eventType ? null : eventType,
      pipelineStep: extractPipelineStepFromEvent(canonicalEventType),
      payload: {
        ...payload,
        pipelineVersion: PIPELINE_VERSION,
      },
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('Unable to record order event', eventType, error);
  }
}

async function upsertOrderFiles(orderRef, orderData, files = [], options = {}) {
  if (!files.length) {
    return;
  }

  const existingFiles = Array.isArray(orderData?.files) ? orderData.files : [];
  const fileMap = new Map(existingFiles.map((file) => [file.type, file]));

  files.forEach((file) => {
    if (!file || !file.type) {
      return;
    }

    const existingEntry = fileMap.get(file.type) || {};
    const normalizedFileStatus = normalizeStatus(file.status || existingEntry.status);
    // Use Date instead of serverTimestamp() - Firestore doesn't support serverTimestamp() inside arrays
    const now = new Date();
    fileMap.set(file.type, {
      ...existingEntry,
      ...file,
      type: file.type,
      status: normalizedFileStatus,
      canonicalStatus: normalizedFileStatus,
      legacyStatus: file.status || existingEntry.legacyStatus || null,
      pipelineVersion: PIPELINE_VERSION,
      createdAt: existingEntry.createdAt || now,
      updatedAt: now,
    });
  });

  const updatedFiles = Array.from(fileMap.values());

  const resolvedUserId =
    orderData?.userId || orderData?.order?.userId || auth.currentUser?.uid || null;

  const payload = {
    userId: resolvedUserId,
    files: updatedFiles,
    pipelineVersion: PIPELINE_VERSION,
    audit: {
      ...(orderData?.audit || {}),
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || orderData?.order?.userId || 'system',
    },
  };

  if (options.stage || options.status) {
    const orderUpdate = {};
    if (options.status) {
      const normalizedOptionStatus = normalizeStatus(options.status);
      orderUpdate.status = normalizedOptionStatus;
      orderUpdate.canonicalStatus = normalizedOptionStatus;
      if (normalizedOptionStatus !== options.status) {
        orderUpdate.legacyStatus = options.status;
      }
    }
    if (options.stage) {
      orderUpdate.stageTimestamps = {
        ...(orderData?.order?.stageTimestamps || {}),
        [options.stage]: serverTimestamp(),
      };
    }
    payload.order = {
      ...orderUpdate,
      userId: payload.userId,
    };
  }

  console.log('About to write to orders collection (upsertOrderFiles):', orderRef.id, 'Payload keys:', Object.keys(payload), 'userId:', payload.userId);
  await setDoc(orderRef, payload, { merge: true });
  console.log('Orders write succeeded (upsertOrderFiles)');

  return updatedFiles;
}

/**
 * Log a behavior event to Firestore
 */
export async function logBehaviorEvent(eventType, eventData = {}) {
  try {
    if (!auth.currentUser) {
      console.warn('Cannot log behavior event: user not authenticated');
      return;
    }

    const canonicalEventType = normalizeEventType(eventType);
    const canonicalContext = buildCanonicalContext({
      userId: auth.currentUser.uid,
      sessionId: eventData.sessionId,
      orderId: eventData.orderId,
      fileName: eventData.fileName,
      filePath: eventData.filePath || eventData.processedPath || eventData.processedImagePath,
    });

    const canonicalStatus =
      eventData.status !== undefined ? normalizeStatus(eventData.status) : undefined;

    const behaviorLog = {
      userId: canonicalContext.userId,
      sessionId: canonicalContext.sessionId,
      orderId: canonicalContext.orderId,
      fileId: canonicalContext.fileId,
      fileName: canonicalContext.fileName,
      eventType: canonicalEventType,
      canonicalEventType,
      legacyEventType: canonicalEventType === eventType ? null : eventType,
      pipelineStep: extractPipelineStepFromEvent(canonicalEventType),
      pipelineVersion: canonicalContext.pipelineVersion,
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      createdAt: serverTimestamp(),
    };

    if (canonicalStatus) {
      behaviorLog.status = canonicalStatus;
      behaviorLog.canonicalStatus = canonicalStatus;
      behaviorLog.legacyStatus = eventData.status;
    }

    if (eventData.ipAddress) {
      behaviorLog.ipAddress = eventData.ipAddress;
    }

    behaviorLog.eventData = stripUndefinedDeep({
      ...eventData,
      legacyEventType: eventType,
      canonicalEventType,
      pipelineVersion: canonicalContext.pipelineVersion,
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    if (canonicalStatus) {
      behaviorLog.eventData.canonicalStatus = canonicalStatus;
    }

    const logRef = doc(collection(db, 'behavior_logs'));
    await setDoc(logRef, behaviorLog);

    console.log(`Behavior event logged: ${eventType}`, behaviorLog);
    return logRef.id;
  } catch (error) {
    console.error('Error logging behavior event:', error);
  }
}

/**
 * Track file upload and processing
 */
export async function trackFileUpload(fileName, filePath, fileSize, sessionId) {
  try {
    if (!auth.currentUser) return;

    const userId = auth.currentUser.uid;
    const context = await ensureOrderContext({ userId, sessionId });
    const canonicalStatus = normalizeStatus('uploaded');

    const fileData = {
      userId,  // CRITICAL
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileId: `${userId}_${fileName}`,
      fileName,
      path: filePath,
      type: 'upload',
      pipelineStep: 'upload',
      pipelineVersion: PIPELINE_VERSION,
      size: fileSize,
      status: canonicalStatus,
      canonicalStatus,
      legacyStatus: 'uploaded',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const fileId = `${userId}_${fileName}`;
    const fileRef = doc(db, FILES_COLLECTION, fileId);
    
    // Check if file exists first
    let fileExists = false;
    try {
      const fileSnap = await getDoc(fileRef);
      fileExists = fileSnap.exists();
    } catch (e) {
      fileExists = false;
    }

    if (fileExists) {
      // Update existing
      await setDoc(fileRef, fileData, { merge: true });
    } else {
      // Create new - no merge
      await setDoc(fileRef, fileData);
    }

    const updatedUploadFiles = await upsertOrderFiles(
      context.orderRef,
      context.orderData,
      [
        {
          type: 'upload',
          path: filePath,
          size: fileSize,
          status: canonicalStatus,
          originalFileName: fileName,
        },
      ],
      { stage: 'upload', status: normalizeStatus('processing') }
    );
    context.orderData.files = updatedUploadFiles;

    await recordOrderEvent(context.orderId, userId, 'file_uploaded', {
      fileName,
      filePath,
      fileSize,
    });

    await logBehaviorEvent('upload_start', {
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileName,
      filePath,
      fileSize,
      status: canonicalStatus,
    });

    return fileId;
  } catch (error) {
    console.error('Error tracking file upload:', error);
  }
}

/**
 * Update file status (processing, completed, failed)
 */
export async function updateFileStatus(fileName, status, additionalData = {}) {
  try {
    if (!auth.currentUser) return;

    const userId = auth.currentUser.uid;
    const context = await ensureOrderContext({ userId, sessionId: additionalData.sessionId });
    const fileRef = doc(db, FILES_COLLECTION, `${userId}_${fileName}`);
    const canonicalStatus = normalizeStatus(status);

    const updateData = {
      userId, // Ensure userId is included for security rules
      status: canonicalStatus,
      canonicalStatus,
      legacyStatus: status,
      pipelineVersion: PIPELINE_VERSION,
      updatedAt: serverTimestamp(),
      ...additionalData,
    };

    await setDoc(fileRef, updateData, { merge: true });

    const fileEntries = [];
    if (additionalData.processedPath || additionalData.processedImagePath) {
      fileEntries.push({
        type: additionalData.fileType || 'processed',
        path: additionalData.processedPath || additionalData.processedImagePath,
        status: canonicalStatus,
        originalFileName: fileName,
      });
    }

    if (fileEntries.length) {
      const updatedFiles = await upsertOrderFiles(
        context.orderRef,
        context.orderData,
        fileEntries,
        {
          stage: canonicalStatus === 'completed' ? 'processing' : 'upload',
          status:
            canonicalStatus === 'completed'
              ? normalizeStatus('processing')
              : normalizeStatus(context.orderData?.order?.status),
        }
      );
      context.orderData.files = updatedFiles;
    }

    await recordOrderEvent(context.orderId, userId, `file_status_${canonicalStatus}`, {
      fileName,
      status: canonicalStatus,
      legacyStatus: status,
      ...additionalData,
    });

    await logBehaviorEvent(`processing_${canonicalStatus}`, {
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileName,
      status: canonicalStatus,
      legacyStatus: status,
      ...additionalData,
    });
  } catch (error) {
    console.error('Error updating file status:', error);
  }
}

/**
 * Track processed image
 */
export async function trackProcessedImage(fileName, processedPath, sessionId) {
  try {
    if (!auth.currentUser) return;

    const userId = auth.currentUser.uid;
    const context = await ensureOrderContext({ userId, sessionId });

    await updateFileStatus(fileName, 'completed', {
      processedPath,
      sessionId: context.sessionId,
      fileType: 'processed',
      pipelineStep: 'processing',
    });

    const updatedProcessedFiles = await upsertOrderFiles(
      context.orderRef,
      context.orderData,
      [
        {
          type: 'processed',
          path: processedPath,
          status: normalizeStatus('completed'),
          originalFileName: fileName,
        },
      ],
      { stage: 'processing', status: normalizeStatus('processing') }
    );
    context.orderData.files = updatedProcessedFiles;

    await recordOrderEvent(context.orderId, userId, 'processing_complete', {
      fileName,
      processedPath,
    });

    await logBehaviorEvent('processing_complete', {
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileName,
      processedPath,
    });
  } catch (error) {
    console.error('Error tracking processed image:', error);
  }
}

/**
 * Track status updates (upload, processing, payment, download)
 */
export async function trackStatus(userId, sessionId, status, type, message = '') {
  try {
    const context = await ensureOrderContext({ userId, sessionId });
    const canonicalStatus = normalizeStatus(status);
    const canonicalType = normalizePipelineType(type);
    const statusData = {
      userId,  // CRITICAL: userId must be included
      sessionId: context.sessionId,
      orderId: context.orderId,
      status: canonicalStatus,
      canonicalStatus,
      legacyStatus: status,
      type: canonicalType,
      canonicalType,
      legacyType: type,
      pipelineVersion: PIPELINE_VERSION,
      message,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Use addDoc for auto-generated IDs (triggers create rule)
    const statusRef = await addDoc(collection(db, STATUS_COLLECTION), statusData);

    await recordOrderEvent(context.orderId, userId, 'status_update', {
      status: canonicalStatus,
      legacyStatus: status,
      type: canonicalType,
      legacyType: type,
      message,
    });

    return statusRef.id;
  } catch (error) {
    console.error('Error tracking status:', error);
  }
}

/**
 * Create or update user/order documents with comprehensive data
 */
export async function createOrUpdateClientOrder(orderData) {
  try {
    console.log('createOrUpdateClientOrder called, auth.currentUser:', auth.currentUser?.uid, 'orderData.userId:', orderData?.userId);
    const user = await waitForAuth();
    if (!user || !user.uid) {
      console.error('createOrUpdateClientOrder: No authenticated user');
      return;
    }

    const userId = user.uid;
    console.log('createOrUpdateClientOrder: Authenticated userId:', userId);
    const context = await ensureOrderContext({ userId, sessionId: orderData.sessionId });
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnapshot = await getDoc(userRef);
    const existingUserData = userSnapshot.exists() ? userSnapshot.data() : {};

    const profile = buildUserProfile(orderData, existingUserData.profile, user);
    const stats = buildUserStats(
      orderData,
      existingUserData.stats,
      context.orderId,
      context.orderData || {}
    );
    const metadata = buildUserMetadata(existingUserData.metadata, { sessionId: context.sessionId });
    const preferences = buildUserPreferences(existingUserData.preferences, orderData);

    const legacyCurrentOrder = buildLegacyCurrentOrder(
      orderData,
      existingUserData.currentOrder,
      context.sessionId
    );

    const userPayload = {
      userId,
      profile,
      stats,
      metadata,
      preferences,
      currentOrder: legacyCurrentOrder,
      email: profile.email,
      displayName: profile.displayName,
      fullName: profile.fullName,
      address: profile.address,
      city: profile.city,
      postcode: profile.postcode,
      country: profile.country,
      photoCount: stats.photoCount,
      totalSpent: stats.totalSpent,
      promoCodesUsed: stats.promoCodesUsed,
      lastActivity: metadata.lastActivity,
    };

    console.log('About to write to users collection:', userId, 'Payload keys:', Object.keys(userPayload));
    await setDoc(userRef, userPayload, { merge: true });
    console.log('Users write succeeded');

    const orderIdentity = {
      userId,
      sessionId: context.sessionId,
      email: profile.email,
      displayName: profile.displayName,
      fullName: profile.fullName,
      address: profile.address,
      city: profile.city,
      postcode: profile.postcode,
      country: profile.country,
    };

    const rawPaymentStatus = orderData.paymentStatus || 'pending';
    const canonicalPaymentStatus = normalizeStatus(rawPaymentStatus);
    const canonicalOrderStatus = canonicalPaymentStatus === 'completed' ? 'completed' : 'processing';
    const legacyOrderStatus = rawPaymentStatus === 'completed' ? 'fulfilled' : 'processing';

    const orderPayload = {
      ...orderIdentity,
      pipelineVersion: PIPELINE_VERSION,
      status: canonicalOrderStatus,
      canonicalStatus: canonicalOrderStatus,
      legacyStatus: legacyOrderStatus,
      order: {
        ...(context.orderData?.order || {}),
        orderId: context.orderId,
        userId,
        sessionId: context.sessionId,
        status: canonicalOrderStatus,
        canonicalStatus: canonicalOrderStatus,
        legacyStatus: legacyOrderStatus,
        stageTimestamps: {
          ...(context.orderData?.order?.stageTimestamps || {}),
          details: serverTimestamp(),
        },
      },
      product: buildProductSection(orderData, context.orderData?.product),
      payment: buildPaymentSection(orderData, context.orderData?.payment),
      delivery: buildDeliverySection(orderData, context.orderData?.delivery, profile.email),
      flags: buildFlagsSection(orderData, context.orderData?.flags),
      audit: {
        ...(context.orderData?.audit || {}),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      },
    };

    const filesToUpsert = [];
    if (orderData.uploadedImagePath) {
      filesToUpsert.push({
        type: 'upload',
        path: orderData.uploadedImagePath,
        status: normalizeStatus('uploaded'),
      });
    }
    if (orderData.processedImagePath) {
      filesToUpsert.push({
        type: 'processed',
        path: orderData.processedImagePath,
        status: 'completed',
      });
    }
    if (orderData.assembledImagePath) {
      filesToUpsert.push({
        type: 'assembled',
        path: orderData.assembledImagePath,
        status: 'completed',
      });
    }

    console.log('About to write to orders collection:', context.orderId, 'Payload keys:', Object.keys(orderPayload), 'userId:', orderPayload.userId);
    await setDoc(context.orderRef, orderPayload, { merge: true });
    console.log('Orders write succeeded');

    context.orderData = await refreshOrderData(context.orderRef);

    if (filesToUpsert.length) {
      const updatedFiles = await upsertOrderFiles(context.orderRef, context.orderData, filesToUpsert);
      context.orderData.files = updatedFiles;
    }

    await recordOrderEvent(context.orderId, userId, 'order_updated', {
      paymentStatus: canonicalPaymentStatus,
      legacyPaymentStatus: rawPaymentStatus,
      photoType: orderData.photoType,
    });

    console.log('User/Order data saved:', { userPayload, orderPayload });
    return context.orderId;
  } catch (error) {
    console.error('Error creating/updating client order:', error);
    throw error;
  }
}

/**
 * Update payment status
 */
export async function updatePaymentStatus(sessionId, paymentStatus, paymentData = {}) {
  try {
    console.log('updatePaymentStatus called, auth.currentUser:', auth.currentUser?.uid, 'paymentData.userId:', paymentData?.userId);
    const user = await waitForAuth();
    if (!user || !user.uid) {
      console.error('updatePaymentStatus: No authenticated user');
      return;
    }

    const userId = user.uid;
    console.log('updatePaymentStatus: Authenticated userId:', userId);
    const context = await ensureOrderContext({ userId, sessionId });
    const canonicalPaymentStatus = normalizeStatus(paymentStatus);

    const existingData = context.orderData || {};
    const paymentMethod = paymentData.paymentMethod ?? existingData.payment?.method;
    const paymentAmount =
      paymentData.amount !== undefined && paymentData.amount !== null
        ? normalizePaymentAmountValue(
            paymentData.amount,
            existingData.payment?.amount ?? 0
          )
        : existingData.payment?.amount;

    const orderUpdate = {
      userId,
      payment: {
        ...existingData.payment,
        status: canonicalPaymentStatus,
        canonicalStatus: canonicalPaymentStatus,
        legacyStatus: paymentStatus,
        method: paymentMethod,
        amount: paymentAmount ?? existingData.payment?.amount ?? 0,
        currency: paymentData.currency || existingData.payment?.currency || 'EUR',
        providerRef: paymentData.providerRef || existingData.payment?.providerRef || null,
        pipelineVersion: PIPELINE_VERSION,
        updatedAt: serverTimestamp(),
      },
      order: {
        ...(existingData.order || {}),
        userId,
        status: canonicalPaymentStatus === 'completed' ? 'completed' : canonicalPaymentStatus,
        canonicalStatus: canonicalPaymentStatus === 'completed' ? 'completed' : canonicalPaymentStatus,
        legacyStatus: paymentStatus === 'completed' ? 'fulfilled' : paymentStatus,
        stageTimestamps: {
          ...(existingData.order?.stageTimestamps || {}),
          payment: serverTimestamp(),
        },
      },
      audit: {
        ...(existingData.audit || {}),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      },
    };

    await setDoc(context.orderRef, orderUpdate, { merge: true });

    context.orderData = await refreshOrderData(context.orderRef);

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnapshot = await getDoc(userRef);
    const userData = userSnapshot.exists() ? userSnapshot.data() : {};
    const updatedLegacyOrder = buildLegacyCurrentOrder(
      {
        paymentStatus: canonicalPaymentStatus,
        paymentMethod,
        paymentAmount,
      },
      userData.currentOrder,
      context.sessionId
    );
    const updatedMetadata = buildUserMetadata(userData.metadata || {}, { sessionId: context.sessionId });
    await setDoc(userRef, {
      metadata: updatedMetadata,
      currentOrder: updatedLegacyOrder,
      paymentStatus: updatedLegacyOrder.paymentStatus,
      paymentMethod: updatedLegacyOrder.paymentMethod,
      paymentAmount: updatedLegacyOrder.paymentAmount,
      lastActivity: updatedMetadata.lastActivity,
    }, { merge: true });

    await recordOrderEvent(context.orderId, userId, `payment_${canonicalPaymentStatus}`, {
      paymentMethod,
      paymentAmount,
      paymentStatus: canonicalPaymentStatus,
      legacyPaymentStatus: paymentStatus,
      ...paymentData,
    });

    await trackStatus(
      userId,
      context.sessionId,
      canonicalPaymentStatus,
      'payment',
      `Payment ${canonicalPaymentStatus}`
    );

    await logBehaviorEvent(`payment_${canonicalPaymentStatus}`, {
      sessionId: context.sessionId,
      orderId: context.orderId,
      paymentStatus: canonicalPaymentStatus,
      legacyPaymentStatus: paymentStatus,
      ...paymentData,
    });
  } catch (error) {
    console.error('Error updating payment status:', error);
  }
}

/**
 * Track download event
 */
export async function trackDownload(fileName, filePath, sessionId) {
  try {
    if (!auth.currentUser) return;

    const userId = auth.currentUser.uid;
    const context = await ensureOrderContext({ userId, sessionId });
    const canonicalStatus = normalizeStatus('completed');

    const downloadFileData = {
      userId,
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileName: `${fileName}_download`,
      path: filePath,
      type: 'download',
      pipelineStep: 'download',
      pipelineVersion: PIPELINE_VERSION,
      status: canonicalStatus,
      canonicalStatus,
      legacyStatus: 'completed',
      originalFileName: fileName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const downloadFileRef = doc(collection(db, FILES_COLLECTION), `${userId}_${fileName}_download`);
    await setDoc(downloadFileRef, downloadFileData, { merge: true });

    const delivery = {
      ...(context.orderData?.delivery || {}),
      downloadUrl: filePath,
      downloadCount: (context.orderData?.delivery?.downloadCount || 0) + 1,
      lastDownloadedAt: serverTimestamp(),
    };

    await setDoc(
      context.orderRef,
      {
        userId,
        delivery,
        order: {
          ...(context.orderData?.order || {}),
          userId,
          status: 'completed',
          canonicalStatus: 'completed',
          legacyStatus: 'fulfilled',
          stageTimestamps: {
            ...(context.orderData?.order?.stageTimestamps || {}),
            delivery: serverTimestamp(),
          },
        },
        audit: {
          ...(context.orderData?.audit || {}),
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        },
      },
      { merge: true }
    );
    context.orderData = await refreshOrderData(context.orderRef);

    const updatedDownloadFiles = await upsertOrderFiles(
      context.orderRef,
      context.orderData,
      [
        {
          type: 'download',
          path: filePath,
          status: canonicalStatus,
          originalFileName: fileName,
        },
      ],
      { stage: 'delivery', status: normalizeStatus('fulfilled') }
    );
    context.orderData.files = updatedDownloadFiles;

    await recordOrderEvent(context.orderId, userId, 'download', {
      fileName,
      filePath,
    });

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnapshot = await getDoc(userRef);
    const userData = userSnapshot.exists() ? userSnapshot.data() : {};
    const refreshedMetadata = buildUserMetadata(userData.metadata || {}, { sessionId: context.sessionId });
    await setDoc(userRef, {
      metadata: refreshedMetadata,
      lastActivity: refreshedMetadata.lastActivity,
    }, { merge: true });

    await logBehaviorEvent('download', {
      sessionId: context.sessionId,
      orderId: context.orderId,
      fileName,
      filePath,
    });
  } catch (error) {
    console.error('Error tracking download:', error);
  }
}
